"use client";

import { useEffect, useRef } from "react";
import type { useCheckout } from "@stripe/react-stripe-js/checkout";

import type { CheckoutServerUpdate } from "./types";

type ReadyCheckout = Extract<
  ReturnType<typeof useCheckout>,
  { type: "success" }
>["checkout"];

/**
 * Runs `update` inside Stripe's `runServerUpdate` whenever its `key` leaves
 * `syncedKey`, one at a time and in order. A failed update (reported through
 * `onError`) or an unmount ends the queue: later updates resolve false without
 * touching Stripe; an unmount also clears pending, so a remounted form owns it.
 * `settled` resolves once the last requested update has.
 */
export function useCheckoutServerSync(
  checkout: ReadyCheckout | null,
  update: CheckoutServerUpdate | undefined,
  onError: () => void
): { settled: () => Promise<boolean> } {
  const queueRef = useRef<Promise<boolean>>(Promise.resolve(true));
  const requestedKeyRef = useRef<string | null>(null);
  const mountedRef = useRef(true);
  const onPendingChangeRef = useRef(update?.onPendingChange);
  onPendingChangeRef.current = update?.onPendingChange;
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      onPendingChangeRef.current?.(false);
    };
  }, []);

  useEffect(() => {
    if (!checkout || !update) return;
    const { key, syncedKey, run, onPendingChange } = update;
    if (key === syncedKey) {
      requestedKeyRef.current = null;
      return;
    }
    if (key === requestedKeyRef.current) return;
    requestedKeyRef.current = key;
    onPendingChange(true);
    const settled = queueRef.current.then(async (live) => {
      if (!live || !mountedRef.current) return false;
      try {
        const result = await checkout.runServerUpdate(run);
        if (result.type === "success") return true;
      } catch {
        // Reported below, like a resolved error.
      }
      onError();
      return false;
    });
    queueRef.current = settled;
    void settled.then(() => {
      if (mountedRef.current && queueRef.current === settled) {
        onPendingChange(false);
      }
    });
  }, [checkout, update, onError]);

  return { settled: () => queueRef.current };
}
